import { useCallback, useMemo } from 'react';

import { EmpPosition, useEmployee } from '~/data/employee';
import { Enum } from '~/data/enums';
import {
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import {
  filterActiveEnumsByCountryAndOrgUnits,
  filterEnumsByCountryAndOrgUnits,
} from '~/utils/helper';

export interface UseEmploymentEnumsFilterOutput {
  filterEnums: (enumList: Enum[], exceptionCode?: string | string[]) => Enum[];
  filterActiveEnums: (
    enumList: Enum[],
    exceptionCode?: string | string[],
  ) => Enum[];
}

export const useEmploymentEnumsFilter = (): UseEmploymentEnumsFilterOutput => {
  const { employee } = useEmployee();
  const { legalEntities } = useAllLegalEntities();

  const {
    internalPositionUnit,
    legalEntityCountry,
  }: {
    internalPositionUnit?: number;
    legalEntityCountry?: string;
  } = useMemo(() => {
    const empPosition: EmpPosition | undefined =
      employee.empPosition ?? employee.empPositionPending;

    if (!empPosition) {
      return {
        internalPositionUnit: undefined,
        legalEntityCountry: undefined,
      };
    }

    const {
      fInternalPositionUnit: internalPositionUnit,
      fEmpLegalEntity: legalEntity,
    } = empPosition;

    const legalEntityCountry = getCountryByLegalEntity(
      legalEntity,
      legalEntities,
    );

    return {
      internalPositionUnit,
      legalEntityCountry,
    };
  }, [legalEntities, employee]);

  const filterEnums = useCallback(
    (enumList: Enum[], exceptionCode?: string | string[]): Enum[] => {
      return filterEnumsByCountryAndOrgUnits(
        enumList,
        legalEntityCountry,
        internalPositionUnit,
        exceptionCode,
      );
    },
    [internalPositionUnit, legalEntityCountry],
  );

  const filterActiveEnums = useCallback(
    (enumList: Enum[], exceptionCode?: string | string[]): Enum[] => {
      return filterActiveEnumsByCountryAndOrgUnits(
        enumList,
        legalEntityCountry,
        internalPositionUnit,
        exceptionCode,
      );
    },
    [internalPositionUnit, legalEntityCountry],
  );
  return {
    filterEnums,
    filterActiveEnums,
  };
};
